import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import axios from 'axios';
import GmLayout from '@/layouts/GmLayout';

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    hue: number;
    order: number;
    is_published: boolean;
    cover_image_url: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Chapter data from the backend.
 */
interface TomeChapter {
    id: number;
    title: string;
    description: string | null;
    order: number;
    pages_count: number;
}

/**
 * Props for the Edit page.
 */
interface Props {
    book: TomeBook;
    chapters: TomeChapter[];
}

/**
 * GM Tome Book Edit page - form for editing a book and managing its chapters.
 */
export default function Edit({ book, chapters }: Props) {
    const [title, setTitle] = useState(book.title);
    const [description, setDescription] = useState(book.description || '');
    const [hue, setHue] = useState(book.hue);
    const [isPublished, setIsPublished] = useState(book.is_published);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Sorting state for chapters
    const [isSorting, setIsSorting] = useState(false);
    const [sortedChapters, setSortedChapters] = useState<TomeChapter[]>(chapters);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    // Drag state for visual feedback
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dragNode = useRef<HTMLDivElement | null>(null);

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.patch(`/gm/tome/books/${book.id}`, {
            title,
            description: description || null,
            hue,
            is_published: isPublished,
        }, {
            onError: (errors) => {
                setErrors(errors);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setIsSubmitting(false);
            },
        });
    };

    // Handle chapter delete
    const handleDeleteChapter = (chapter: TomeChapter) => {
        if (confirm(`Are you sure you want to delete "${chapter.title}"? This will also delete all ${chapter.pages_count} pages within it.`)) {
            router.delete(`/gm/tome/books/${book.id}/chapters/${chapter.id}`, { preserveScroll: true });
        }
    };

    // Enter sort mode
    const startSorting = () => {
        setSortedChapters([...chapters]);
        setIsSorting(true);
    };

    // Cancel sorting and reset to original order
    const cancelSorting = () => {
        setSortedChapters([...chapters]);
        setIsSorting(false);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Save the new order to the backend using axios (since endpoint returns JSON, not Inertia response)
    const saveOrder = async () => {
        setIsSavingOrder(true);

        // Build the payload with chapter IDs and their new order values
        const chaptersPayload = sortedChapters.map((ch, index) => ({
            id: ch.id,
            order: index,
        }));

        try {
            await axios.post(`/gm/tome/books/${book.id}/chapters/reorder`, { chapters: chaptersPayload });

            // Reload the page to get fresh data from the server
            router.reload({ only: ['chapters'], preserveScroll: true });

            setIsSorting(false);
            setIsSavingOrder(false);
        } catch {
            setIsSavingOrder(false);
            alert('Failed to save chapter order. Please try again.');
        }
    };

    // Handle drag start
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedIndex(index);
        dragNode.current = e.currentTarget;

        // Set drag image and data
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());

        // Add a slight delay to allow the drag image to be set before adding styles
        setTimeout(() => {
            if (dragNode.current) {
                dragNode.current.style.opacity = '0.5';
            }
        }, 0);
    };

    // Handle drag over another item
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedIndex === null || draggedIndex === index) return;

        setDragOverIndex(index);
    };

    // Handle drag end (cleanup)
    const handleDragEnd = () => {
        if (dragNode.current) {
            dragNode.current.style.opacity = '1';
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragNode.current = null;
    };

    // Handle drop
    const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            handleDragEnd();
            return;
        }

        // Reorder the chapters array
        const newChapters = [...sortedChapters];
        const [draggedChapter] = newChapters.splice(draggedIndex, 1);
        newChapters.splice(dropIndex, 0, draggedChapter);

        setSortedChapters(newChapters);
        handleDragEnd();
    };

    // Get the chapters to display (sorted or original)
    const displayChapters = isSorting ? sortedChapters : chapters;

    // Generate preview color from hue
    const accentColor = `hsl(${hue}, 60%, 40%)`;

    return (
        <GmLayout title={`Edit: ${book.title}`} activePath="/gm/tome/books">
            <div className="max-w-4xl">
                <div className="flex items-center gap-4 mb-6">
                    <Link href="/gm/tome/books" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Books
                    </Link>
                </div>

                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">📚 Edit Book</h1>
                    <a
                        href={`/tome/${book.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    >
                        View Book
                    </a>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Book Details Form */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Book Details</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    required
                                />
                                {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                />
                            </div>

                            {/* Hue */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Accent Color (Hue: {hue}°)</label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range" min="0" max="360" value={hue}
                                        onChange={(e) => setHue(parseInt(e.target.value))}
                                        className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                                        style={{ background: 'linear-gradient(to right, hsl(0,60%,50%), hsl(60,60%,50%), hsl(120,60%,50%), hsl(180,60%,50%), hsl(240,60%,50%), hsl(300,60%,50%), hsl(360,60%,50%))' }}
                                    />
                                    <div className="w-8 h-8 rounded border border-slate-600" style={{ backgroundColor: accentColor }} />
                                </div>
                            </div>

                            {/* Published */}
                            <div className="flex items-center gap-3">
                                <input type="checkbox" id="is_published" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} className="w-4 h-4 rounded" />
                                <label htmlFor="is_published" className="text-gray-300">Published</label>
                            </div>

                            <button type="submit" disabled={isSubmitting} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 text-white rounded transition-colors">
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </div>

                    {/* Chapters Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Chapters</h2>
                            <div className="flex items-center gap-2">
                                {/* Sort mode controls */}
                                {isSorting ? (
                                    <>
                                        <button
                                            onClick={saveOrder}
                                            disabled={isSavingOrder}
                                            className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white rounded transition-colors"
                                        >
                                            {isSavingOrder ? 'Saving...' : 'Save Order'}
                                        </button>
                                        <button
                                            onClick={cancelSorting}
                                            disabled={isSavingOrder}
                                            className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white rounded transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {/* Only show Sort button if there are 2+ chapters */}
                                        {chapters.length >= 2 && (
                                            <button
                                                onClick={startSorting}
                                                className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                                            >
                                                Sort Chapters
                                            </button>
                                        )}
                                        <Link
                                            href={`/gm/tome/books/${book.id}/chapters/create`}
                                            className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                                        >
                                            + Add Chapter
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Sort mode instructions */}
                        {isSorting && (
                            <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700/50 rounded text-sm text-blue-200">
                                Drag chapters to reorder them. Click "Save Order" when done.
                            </div>
                        )}

                        {chapters.length === 0 ? (
                            <p className="text-gray-400 text-sm">No chapters yet. Add a chapter to start organizing pages.</p>
                        ) : (
                            <div className="space-y-2">
                                {displayChapters.map((chapter, index) => (
                                    <div
                                        key={chapter.id}
                                        draggable={isSorting}
                                        onDragStart={(e) => isSorting && handleDragStart(e, index)}
                                        onDragOver={(e) => isSorting && handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDrop={(e) => isSorting && handleDrop(e, index)}
                                        className={`flex items-center justify-between p-3 bg-slate-700 rounded transition-all ${
                                            isSorting ? 'cursor-grab active:cursor-grabbing' : ''
                                        } ${
                                            draggedIndex === index ? 'opacity-50' : ''
                                        } ${
                                            dragOverIndex === index && draggedIndex !== index
                                                ? 'border-2 border-purple-500 border-dashed'
                                                : 'border-2 border-transparent'
                                        }`}
                                    >
                                        {/* Drag handle - only visible in sort mode */}
                                        {isSorting && (
                                            <div className="mr-3 text-gray-400 select-none">
                                                <span className="text-lg">⋮⋮</span>
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-medium truncate">{chapter.title}</p>
                                            <p className="text-xs text-gray-400">{chapter.pages_count} pages</p>
                                        </div>
                                        {/* Hide Edit and Delete buttons in sort mode to prevent accidental actions */}
                                        {!isSorting && (
                                            <div className="flex gap-2 ml-2">
                                                <Link
                                                    href={`/gm/tome/books/${book.id}/chapters/${chapter.id}/edit`}
                                                    className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                                                >
                                                    Edit
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteChapter(chapter)}
                                                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Slug Info */}
                <div className="mt-6 text-sm text-gray-500">
                    <p>Slug: <code className="bg-slate-700 px-2 py-1 rounded">{book.slug}</code></p>
                    <p className="mt-1">URL: <code className="bg-slate-700 px-2 py-1 rounded">/tome/{book.slug}</code></p>
                </div>
            </div>
        </GmLayout>
    );
}
